import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Shield, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, fmtTime } from "@/components/PageHeader";
import { ThespianRankCard } from "@/components/thespian/ThespianRankCard";
import { useFeatureFlags } from "@/lib/featureFlags";
import ThespianClaimsList from "@/components/thespian/ThespianClaimsList";
import { shortNo } from "@/lib/caseNo";

export default function ParentStudentRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { merit_tracking: meritOn, lettering: letteringOn } = useFeatureFlags();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [th, setTh] = useState(null);

  useEffect(() => {
    api.get(`/parent/students/${id}`)
      .then(({ data }) => setDetail(data))
      .catch((err) => { toast.error(formatApiError(err.response?.data?.detail)); navigate("/parent/dashboard"); })
      .finally(() => setLoading(false));
    api.get(`/parent/students/${id}/thespian`).then(({ data }) => setTh(data)).catch(() => setTh(null));
  }, [id, navigate]);

  if (loading) return <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!detail) return null;

  const ls = detail.letter_status || {};
  const b = detail.breakdown || {};
  const awards = (ls.first_letter_earned ? 1 : 0) + (ls.additional_awards || 0);
  const stat = (label, value, cls) => (
    <div className="rounded-lg border border-slate-200 p-3">
      <p className={`text-2xl font-extrabold tabular-nums ${cls}`}>{value}</p>
      <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    </div>
  );

  return (
    <div data-testid="parent-student-record">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {meritOn && <>
        {stat("Merits", b.total_merits ?? 0, "text-emerald-600")}
        {stat("Demerits", b.total_demerits ?? 0, "text-red-600")}
        {stat("Net", b.net ?? 0, (b.net ?? 0) < 0 ? "text-red-600" : "text-slate-800")}
        </>}
        {letteringOn && <div className="rounded-lg border border-slate-200 p-3" data-testid="parent-lettering-stat">
          <p className="text-2xl font-extrabold tabular-nums text-violet-700">{ls.total_approved_points || 0}</p>
          <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Lettering</p>
          <div className="mt-1 flex gap-0.5" title={`${awards} letter award${awards === 1 ? "" : "s"} earned`}>
            {[1, 2, 3, 4].map((i) => (
              <Shield key={i} className={`h-4 w-4 ${i <= awards ? "fill-violet-100 text-violet-600" : "text-slate-300"}`} />
            ))}
          </div>
        </div>}
      </div>

      {meritOn && <div className="mt-6">
        <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-500">Merit &amp; Demerit Cases</h4>
        {detail.cases.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-400" data-testid="parent-cases-empty">No merit or demerit cases.</p>
        ) : (
          <Card className="border-slate-200 shadow-sm overflow-x-auto">
            <Table data-testid="parent-cases-table">
              <TableHeader><TableRow className="bg-slate-50">{["Case #", "Date", "Type", "Value", "Status"].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {detail.cases.map((c) => (
                  <TableRow key={c.id} data-testid={`parent-case-${c.case_number}`}>
                    <TableCell className="font-mono text-xs font-semibold">{shortNo(c.case_number)}</TableCell>
                    <TableCell className="text-sm">{fmtDate(c.date_issued)}</TableCell>
                    <TableCell><StatusBadge status={c.case_type} /></TableCell>
                    <TableCell className={`font-bold tabular-nums ${c.value > 0 ? "text-emerald-600" : "text-red-600"}`}>{c.value > 0 ? `+${c.value}` : c.value}</TableCell>
                    <TableCell>
                      <StatusBadge status={c.status} />
                      {c.conference_date && (
                        <p className="mt-1 text-xs font-medium text-blue-600">
                          Conference {fmtDate(c.conference_date)}{c.conference_time ? ` at ${fmtTime(c.conference_time)}` : ""}
                        </p>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>}

      {letteringOn && <div className="mt-6">
        <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-500">Lettering</h4>
        {detail.lettering.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-400" data-testid="parent-lettering-empty">No lettering submissions.</p>
        ) : (
          <Card className="border-slate-200 shadow-sm overflow-x-auto">
            <Table data-testid="parent-lettering-table">
              <TableHeader><TableRow className="bg-slate-50">{["Case #", "Event Date", "Achievement", "Points", "Status"].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {detail.lettering.map((s) => (
                  <TableRow key={s.id} data-testid={`parent-lettering-${s.case_number}`}>
                    <TableCell className="font-mono text-xs font-semibold">{shortNo(s.case_number)}</TableCell>
                    <TableCell className="text-sm">{fmtDate(s.event_date)}</TableCell>
                    <TableCell className="text-sm text-slate-700">{s.achievement_label_snapshot}</TableCell>
                    <TableCell className="font-bold tabular-nums text-amber-600">{s.points_requested_snapshot}</TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>}

      {th?.enabled && (
        <div className="mt-6" data-testid="parent-thespian-section">
          <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-500">Thespian Points</h4>
          <div className="mb-4"><ThespianRankCard summary={th} /></div>
          {th.items.length === 0
            ? <p className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-400" data-testid="parent-thespian-empty">No Thespian point claims yet.</p>
            : <ThespianClaimsList items={th.items} />}
        </div>
      )}
    </div>
  );
}
